import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { Loader2, ToggleLeft } from "lucide-react";
import { toast } from "sonner";

import { GET_FEATURE_FLAGS } from "@/graphql/queries/features";
import { SET_FEATURE_FLAG } from "@/graphql/mutations/features";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

type Account = {
  /** Unique per flag and account — the cache key, not something to send back. */
  id: string;
  /** The account itself, which is what the mutation takes. */
  accountId: string;
  name: string;
  email: string | null;
  role: string;
  learnerCount: number;
  enabled: boolean;
};

type Flag = {
  key: string;
  name: string;
  description: string;
  enabledCount: number;
  accounts: Account[];
};

/**
 * Which households have which features.
 *
 * Rows are grown-up accounts, not learners: a flag governs a household, so a
 * parent with three children appears once and their learners follow. Admin
 * only — a guardian session is often the one left open on the device a child
 * practises on, so a guardian-reachable switch is one a child is one request
 * away from.
 */
export default function FeatureFlagsPage() {
  const { data, loading } = useQuery(GET_FEATURE_FLAGS, {
    fetchPolicy: "cache-and-network",
  });
  const [setFlag] = useMutation(SET_FEATURE_FLAG);
  const [pending, setPending] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  const flags: Flag[] = (data as any)?.featureFlags ?? [];

  const toggle = async (flag: Flag, account: Account) => {
    setPending(account.id);
    try {
      const result: any = await setFlag({
        variables: {
          feature: flag.key,
          studentId: account.accountId,
          enabled: !account.enabled,
        },
        // The mutation returns the whole list, and every object in it carries an
        // id, so Apollo merges the post-change values straight into the query's
        // copy — no refetch to race the toggle, and no stale count left behind.
      });
      const errors = result?.data?.setFeatureFlag?.errors ?? [];
      if (errors.length > 0) {
        toast.error(errors[0]);
        return;
      }
      toast.success(
        `${flag.name}: ${account.enabled ? "išjungta" : "įjungta"} — ${account.name}`
      );
    } catch (error) {
      toast.error("Nepavyko pakeisti");
      console.error(error);
    } finally {
      setPending(null);
    }
  };

  if (loading && flags.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const needle = filter.trim().toLowerCase();

  return (
    <div className="mx-auto max-w-4xl space-y-6 pb-16">
      <header className="space-y-2">
        <div className="flex items-center gap-2">
          <ToggleLeft className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold tracking-tight">Funkcijos</h1>
        </div>
        <p className="text-muted-foreground">
          Funkcijos jungiamos suaugusiojo paskyrai — kartu su visais jos mokiniais.
        </p>
      </header>

      <Input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Ieškoti paskyros…"
        className="max-w-sm"
      />

      {flags.map((flag) => {
        const accounts = flag.accounts.filter(
          (a) =>
            !needle ||
            a.name.toLowerCase().includes(needle) ||
            (a.email ?? "").toLowerCase().includes(needle)
        );

        return (
          <Card key={flag.key}>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-lg">{flag.name}</CardTitle>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {flag.description}
                  </p>
                </div>
                <Badge variant={flag.enabledCount > 0 ? "default" : "secondary"}>
                  {flag.enabledCount} įjungta
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-1">
              {accounts.length === 0 && (
                <p className="py-4 text-sm text-muted-foreground">
                  Paskyrų nerasta.
                </p>
              )}
              {accounts.map((account) => (
                <div
                  key={account.id}
                  className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-muted/50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{account.name}</span>
                      {account.role === "admin" && (
                        <Badge variant="outline" className="text-xs">
                          admin
                        </Badge>
                      )}
                    </div>
                    <div className="truncate text-sm text-muted-foreground">
                      {account.email || "—"}
                      {account.learnerCount > 0 &&
                        ` · ${account.learnerCount} mokin${
                          account.learnerCount === 1 ? "ys" : "iai"
                        }`}
                    </div>
                  </div>

                  <Button
                    size="sm"
                    variant={account.enabled ? "default" : "outline"}
                    disabled={pending === account.id}
                    onClick={() => toggle(flag, account)}
                  >
                    {pending === account.id && (
                      <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                    )}
                    {account.enabled ? "Įjungta" : "Išjungta"}
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
